import { useState } from 'react';

import {
  useDismissAllUnmatchedReplies,
  useDismissUnmatchedReply,
  useLinkUnmatchedReply,
  useUnmatchedReplies,
} from '../../hooks/useOutreach';
import type { UnmatchedReply } from '../../types/outreach';
import { formatEtShort } from '../../utils/formatTimestamp';
import styles from '../pages/OutreachPage.module.css';


/**
 * Unmatched replies sub-view: queue of inbound replies that the auto-matcher
 * couldn't attribute (no In-Reply-To header match and no fallback by sender
 * email). Admin manually pastes the campaign-recipient ID -- found in the
 * Recipients tab of the relevant campaign -- to link.
 *
 * Or, if the row isn't actually a reply (newsletter that slipped through,
 * cold pitch with mangled headers), use Dismiss to clear the row from
 * triage. Dismiss-all clears every row in one shot — used for clearing
 * historical pollution.
 */
export function UnmatchedRepliesAdmin() {
  const repliesQ = useUnmatchedReplies();
  const dismissAllMut = useDismissAllUnmatchedReplies();

  async function handleDismissAll() {
    const count = repliesQ.data?.length ?? 0;
    if (count === 0) return;
    if (
      !window.confirm(
        `Dismiss all ${count} unmatched replies? They'll be permanently ` +
        'deleted from the triage queue. Real replies that were attributed ' +
        'to a recipient are NOT affected.',
      )
    ) {
      return;
    }
    try {
      const res = await dismissAllMut.mutateAsync();
      alert(`Dismissed ${res.deleted} unmatched replies.`);
    } catch (e: unknown) {
      alert(`Dismiss failed: ${e instanceof Error ? e.message : 'unknown'}`);
    }
  }

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Unmatched replies</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span className={styles.helpText}>
            {repliesQ.data
              ? `${repliesQ.data.length} pending`
              : ''}
          </span>
          {(repliesQ.data?.length ?? 0) > 0 && (
            <button
              type="button"
              className={`${styles.button} ${styles.buttonDanger}`}
              onClick={handleDismissAll}
              disabled={dismissAllMut.isPending}
              title={
                'Permanently delete every row in the triage queue. ' +
                'Real attributed replies are not affected.'
              }
            >
              {dismissAllMut.isPending ? 'Dismissing…' : 'Dismiss all'}
            </button>
          )}
        </div>
      </div>

      {repliesQ.isLoading ? (
        <div className={styles.empty}>Loading…</div>
      ) : repliesQ.data && repliesQ.data.length === 0 ? (
        <div className={styles.empty}>
          No unmatched replies. Inbound mail is being auto-attributed
          successfully.
        </div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Received</th>
              <th>From</th>
              <th>Body excerpt</th>
              <th>Source</th>
              <th>Headers</th>
              <th>Link to recipient</th>
            </tr>
          </thead>
          <tbody>
            {repliesQ.data?.map((r) => (
              <ReplyRow key={r.id} reply={r} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}


function ReplyRow({ reply }: { reply: UnmatchedReply }) {
  const linkMut = useLinkUnmatchedReply();
  const dismissMut = useDismissUnmatchedReply();
  const [recipientIdInput, setRecipientIdInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function handleLink() {
    setError(null);
    const recipientId = Number.parseInt(recipientIdInput, 10);
    if (!Number.isFinite(recipientId) || recipientId <= 0) {
      setError('Enter a numeric recipient ID.');
      return;
    }
    try {
      await linkMut.mutateAsync({ replyId: reply.id, recipientId });
      setRecipientIdInput('');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Link failed.');
    }
  }

  async function handleDismiss() {
    setError(null);
    if (
      !window.confirm(
        `Dismiss this reply from ${reply.from_email}? It will be ` +
        'permanently deleted from the triage queue.',
      )
    ) {
      return;
    }
    try {
      await dismissMut.mutateAsync(reply.id);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Dismiss failed.');
    }
  }

  return (
    <tr>
      <td className={styles.code}>
        {formatEtShort(reply.received_at)}
      </td>
      <td className={styles.code}>{reply.from_email}</td>
      <td style={{ maxWidth: 360 }}>
        {reply.body_excerpt ? (
          <span style={{ fontSize: 12, color: '#444' }}>
            {reply.body_excerpt.slice(0, 200)}
            {reply.body_excerpt.length > 200 ? '…' : ''}
          </span>
        ) : (
          <em style={{ color: '#999' }}>—</em>
        )}
      </td>
      <td className={styles.code}>{reply.source}</td>
      <td className={styles.code} style={{ maxWidth: 240, fontSize: 11 }}>
        {reply.in_reply_to && (
          <div title={reply.in_reply_to}>
            in-reply-to: {reply.in_reply_to.slice(0, 40)}
            {reply.in_reply_to.length > 40 ? '…' : ''}
          </div>
        )}
        {reply.internet_message_id && (
          <div
            title={reply.internet_message_id}
            style={{ color: '#888' }}
          >
            id: {reply.internet_message_id.slice(0, 40)}
            {reply.internet_message_id.length > 40 ? '…' : ''}
          </div>
        )}
      </td>
      <td>
        <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
          <input
            className={styles.input}
            type="text"
            inputMode="numeric"
            placeholder="Recipient ID"
            value={recipientIdInput}
            onChange={(e) => setRecipientIdInput(e.target.value)}
            style={{ width: 110 }}
            disabled={linkMut.isPending}
          />
          <button
            type="button"
            className={`${styles.button} ${styles.buttonPrimary}`}
            onClick={handleLink}
            disabled={linkMut.isPending || !recipientIdInput.trim()}
          >
            {linkMut.isPending ? 'Linking…' : 'Link'}
          </button>
          <button
            type="button"
            className={styles.button}
            onClick={handleDismiss}
            disabled={dismissMut.isPending}
            title="Permanently delete this row from the triage queue (use when it isn't a real reply)"
          >
            {dismissMut.isPending ? 'Dismissing…' : 'Dismiss'}
          </button>
        </div>
        {error && (
          <div
            className={styles.helpText}
            style={{ color: '#B04030', marginTop: 4 }}
          >
            {error}
          </div>
        )}
      </td>
    </tr>
  );
}
